import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { PermissionEnum } from '@core/permissions/permission.enum';
import {
  abgFollowUpRoute,
  errorResourceNotFound,
  imageViewerRoute,
  loginRoute,
  logoutRoute,
  maintenancePageRoute,
  nutritionPilotRoute,
  nutritionRoute,
  offlineAppRoute,
  orthognathicRoute,
  orthoQaRoute,
  orthoRoute,
  partnersRoute,
  patientRoute,
  patientsRoute,
  practitioners,
  recordAdministrationRoute,
  reportsMainRoute,
  resourcesRoute,
  reviewRoute,
  settingsRoute,
  speechQaRoute,
  speechRoute,
  surgicalRoute,
  toolsRoute,
  treatmentCentersRoute,
  usersRoute
} from '@utils/routing.utils';
import { AuthGuard } from './features/authentication/helpers/auth.guard';
import { PermissionGuard } from './features/authentication/helpers/permission.guard';
import { EditSettingsComponent, EditSettingsComponentModule } from './features/edit-settings/edit-settings.component';
import { ResourceNotFoundPageComponent } from './features/resource-not-found-page/resource-not-found-page.component';
import { TreatmentCenterDetailsPageComponent } from './features/treatment-center/components/treatment-center-details-page/treatment-center-details-page.component';
import { NotFoundErrorType } from '@src/app/features/resource-not-found-page/enums/not-found-error.type';
import { MaintenancePageComponent } from '@core/maintenance/components/maintenance-page.component';
import { AuthContainerComponent } from '@src/app/features/authentication/components/auth-container/auth-container.component';
import { LogoutComponent } from '@src/app/features/authentication/components/logout/logout.component';

const routes: Routes = [
  {
    path: '',
    loadChildren: () => import('./features/dashboard/dashboard-routing.module').then(m => m.DashboardRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: settingsRoute,
    component: EditSettingsComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { newStyles: true, permission: PermissionEnum.EDIT_USER_SETTINGS }
  },
  {
    path: patientsRoute,
    loadChildren: () => import('./features/patient-search/patient-search-routing.module').then(m => m.PatientSearchRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: patientRoute,
    loadChildren: () => import('./features/patient/patient-routing.module').then(m => m.PatientRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: surgicalRoute,
    loadChildren: () => import('./features/surgical/surgical-routing.module').then(m => m.SurgicalRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: abgFollowUpRoute,
    loadChildren: () => import('./features/abg-follow-up/abg-follow-up-routing.module').then(m => m.AbgFollowUpRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: orthoQaRoute,
    loadChildren: () => import('./features/ortho-qa/ortho-qa-routing.module').then(m => m.OrthoQaRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: speechQaRoute,
    loadChildren: () => import('./features/speech-qa/speech-qa-routing.module').then(m => m.SpeechQaRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: speechRoute,
    loadChildren: () => import('./features/speech/speech-routing.module').then(m => m.SpeechRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: orthognathicRoute,
    loadChildren: () => import('./features/orthognathic/orthognathic-routing.module').then(m => m.OrthognathicRoutingModule)
  },
  {
    path: orthoRoute,
    loadChildren: () => import('./features/ortho/ortho-routing.module').then(m => m.OrthoRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: nutritionRoute,
    loadChildren: () => import('./features/nutrition-management/nutrition-routing.module').then(m => m.NutritionRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: nutritionPilotRoute,
    loadChildren: () => import('./features/nutrition/nutrition-historical-routing.module').then(m => m.NutritionHistoricalRoutingModule),
    canActivate: [AuthGuard]
  },
  {
    path: partnersRoute,
    loadChildren: () => import('./features/partner/partner.module').then(m => m.PartnerModule),
    canActivate: [AuthGuard, PermissionGuard],
    canLoad: [AuthGuard],
    data: { permission: PermissionEnum.GET_PARTNERS_RECORDS }
  },
  {
    path: practitioners,
    loadChildren: () => import('./features/practitioner/practitioner.module').then(m => m.PractitionerModule),
    canActivate: [AuthGuard, PermissionGuard],
    canLoad: [AuthGuard],
    data: { permission: PermissionEnum.GET_PRACTITIONERS }
  },
  {
    path: usersRoute,
    loadChildren: () => import('./features/user/users.module').then(m => m.UsersModule),
    canActivate: [AuthGuard, PermissionGuard],
    canLoad: [AuthGuard],
    data: { permission: PermissionEnum.GET_USERS }
  },
  {
    path: offlineAppRoute,
    loadChildren: () => import('./features/offline-app/offline-app-routing.module').then(m => m.OfflineAppRoutingModule),
    canActivate: [AuthGuard],
    canLoad: [AuthGuard]
  },
  {
    path: toolsRoute,
    loadChildren: () => import('./features/tools/tools-routing.module').then(m => m.ToolsRoutingModule),
    canActivate: [AuthGuard],
    canLoad: [AuthGuard]
  },
  {
    path: resourcesRoute,
    loadChildren: () =>
      import('./features/educational-resources/educational-resources-routing.module').then(m => m.EducationalResourcesRoutingModule),
    canActivate: [AuthGuard],
    canLoad: [AuthGuard]
  },
  {
    path: recordAdministrationRoute,
    loadChildren: () =>
      import('./features/record-administration/record-administration-routing.module').then(m => m.RecordAdministrationRoutingModule),
    canActivate: [AuthGuard],
    canLoad: [AuthGuard]
  },
  {
    path: `${treatmentCentersRoute}/:id`,
    component: TreatmentCenterDetailsPageComponent,
    canActivate: [AuthGuard, PermissionGuard],
    canLoad: [AuthGuard],
    data: { permission: PermissionEnum.GET_TREATMENT_CENTER_DETAILS, newStyles: true }
  },
  {
    path: reviewRoute,
    loadChildren: () => import('./features/review/review-routing.module').then(m => m.ReviewsRoutingModule),
    canActivate: [AuthGuard],
    canLoad: [AuthGuard]
  },
  {
    path: reportsMainRoute,
    loadChildren: () => import('./features/reports/reports-routing.module').then(m => m.ReportsRoutingModule),
    canLoad: [AuthGuard]
  },
  {
    path: imageViewerRoute,
    loadChildren: () => import('./features/image-viewer/image-viewer.module').then(m => m.ImageViewerModule),
    canActivate: [AuthGuard],
    canLoad: [AuthGuard]
  },
  {
    path: 'select-profile',
    loadComponent: () => import('./features/user/components/select-profile/select-profile.component').then(m => m.SelectProfileComponent),
    canActivate: [AuthGuard]
  },
  {
    path: maintenancePageRoute,
    component: MaintenancePageComponent
  },
  {
    path: errorResourceNotFound,
    component: ResourceNotFoundPageComponent,
    data: { errorType: NotFoundErrorType.RESOURCE_NOT_FOUND }
  },
  {
    path: loginRoute,
    component: AuthContainerComponent
  },
  {
    path: logoutRoute,
    component: LogoutComponent
  },
  {
    path: '**',
    component: ResourceNotFoundPageComponent,
    data: { errorType: NotFoundErrorType.URL_NOT_FOUND }
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top'
    }),
    EditSettingsComponentModule
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
